import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Popover,
  Button,
  useTheme,
  TextField,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  ReportOutlined as ReportIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PyrenzBlueButton } from '@client/theme';

interface CharacterProfileProps {
  character: any;
  isLoading: any;
  creatorUuid: string | null;
  isLoggedIn: boolean;
  handleStartChat: () => void;
  handleEditCharacter: () => void;
  handleCharacterDeletion: () => void;
  handleCreatorClick: () => void;
  handleReportCharacter: (reportMessage: string, creatorUuid: string) => void;
}

export function CharacterProfile({
  character,
  isLoading,
  creatorUuid,
  isLoggedIn,
  handleStartChat,
  handleEditCharacter,
  handleCharacterDeletion,
  handleCreatorClick,
  handleReportCharacter,
}: CharacterProfileProps) {
  const isCreator = creatorUuid === character.creator_uuid;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reportText, setReportText] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  const handleOpenReport = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleCloseReport = () => {
    setAnchorEl(null);
    setReportText('');
  };
  const handleSubmitReport = () => {
    handleReportCharacter(reportText, character.creator_uuid);
    handleCloseReport();
  };

  const reportPopoverOpen = Boolean(anchorEl);

  return (
    <Card sx={{ 
      width: '100%', 
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      color: '#fff', 
      borderRadius: 3, 
      boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
    }}>
      <CardMedia
        component="img"
        height="300"
        image={character.profile_image}
        alt={character.name}
        sx={{ 
          borderTopLeftRadius: 12, 
          borderTopRightRadius: 12,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      />
      <CardContent>
        <Typography gutterBottom variant="h5" component="h2" sx={{ fontWeight: 600, color: '#fff' }}>
          {character.name}
        </Typography>
        <Typography variant="body2" display="flex" alignItems="center" sx={{ mb: 2, color: 'rgba(255,255,255,0.6)' }}>
          <PersonIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.6)' }} />
          Created by:&nbsp;
          <Typography
            component="span"
            sx={{
              color: '#667eea',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={handleCreatorClick}
          >
            {character.creator}
          </Typography>
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {character.tags?.map((tag: string) => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => navigate(`/home?tag=${encodeURIComponent(tag)}`)}
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                color: '#667eea',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  backgroundColor: 'rgba(102, 126, 234, 0.25)', 
                  boxShadow: '0 0 10px rgba(102, 126, 234, 0.4)', 
                  transform: 'scale(1.05)' 
                },
              }}
            />
          ))}
        </Box>
        <PyrenzBlueButton
          variant="contained"
          sx={{ mt: 3, py: 1.5 }}
          fullWidth
          onClick={handleStartChat}
          disabled={isLoading.startChat}
          endIcon={isLoggedIn ? <ArrowForwardIcon /> : <LockIcon />}
        >
          {isLoading.startChat ? 'Loading...' : isLoggedIn ? 'Start Chat' : 'Login to Chat'}
        </PyrenzBlueButton>

        {!character.is_private && isLoggedIn && (
          <>
            <PyrenzBlueButton
              variant="outlined"
              sx={{ mt: 2, py: 1.5, borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }}
              fullWidth
              onClick={handleOpenReport}
              startIcon={<ReportIcon />}
            >
              Report Character
            </PyrenzBlueButton>
            <Popover
              open={reportPopoverOpen}
              anchorEl={anchorEl}
              onClose={handleCloseReport}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  backgroundColor: 'rgba(26, 26, 46, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 20px 40px -10px rgba(0,0,0,0.5)',
                  borderRadius: 2,
                }
              }}
            >
              <Box sx={{ p: 3, color: '#fff', width: 400 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Why do you want to report this character?
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  placeholder="Type your reason here..."
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  fullWidth
                  sx={{
                    mt: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      borderRadius: 2,
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button 
                    variant="contained" 
                    onClick={handleSubmitReport}
                    sx={{ 
                      backgroundColor: '#ef5350',
                      '&:hover': { backgroundColor: '#d32f2f' }
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Popover>
          </>
        )}

        {isCreator && (
          <>
            <PyrenzBlueButton
              variant="outlined"
              sx={{ mt: 2, py: 1.5, borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }}
              fullWidth
              onClick={handleEditCharacter}
              disabled={isLoading.editCharacter}
              startIcon={<EditIcon />}
            >
              {isLoading.editCharacter ? 'Loading...' : 'Edit Character'}
            </PyrenzBlueButton>
            <PyrenzBlueButton
              variant="outlined"
              sx={{ mt: 2, py: 1.5, borderColor: '#ef5350', color: '#ef5350' }}
              fullWidth
              onClick={handleCharacterDeletion}
              disabled={isLoading.deleteCharacter}
              startIcon={<DeleteIcon />}
            >
              {isLoading.deleteCharacter ? 'Loading...' : 'Delete Character'}
            </PyrenzBlueButton>
          </>
        )}
      </CardContent>
    </Card>
  );
}